import { GemaRequestError, classifyRequestError, type RequestPhase } from "./requestError";
import { supabase } from "@/integrations/supabase/client";

export interface RamonProgress {
  rulesVersion: string;
  today: string;
  timezone: string;
  streakDays: number;
  checkedToday: boolean;
  days: Array<{ date: string; checked: boolean; sequenceDay?: number }>;
}
const root = `${import.meta.env.VITE_API_URL || "https://api.ondiet.app"}/api/meals/ramon`;
export async function request(path: string, method = "GET", body?: unknown, timeoutMs = 12000,
  options?: { signal?: AbortSignal; onPhase?: (phase: RequestPhase) => void }) {
  let owner: string | undefined;
  let phase: RequestPhase = 'session';
  let activeTimeout: AbortSignal | undefined;
  const enter = (next: RequestPhase) => { phase = next; options?.onPhase?.(next); };
  const checkCancelled = () => { if (options?.signal?.aborted) throw new GemaRequestError('cancelled', phase); };
  const cleanups: Array<() => void> = [];
  const send = async () => {
    enter('session'); checkCancelled();
    const { data: { session } } = await supabase.auth.getSession();
    checkCancelled();
    if (!session || (owner && session.user.id !== owner)) throw new GemaRequestError('session', phase);
    owner = session.user.id;
    enter('fetch');
    const timeout = AbortSignal.timeout(timeoutMs);
    activeTimeout = timeout;
    const controller = new AbortController();
    const abort = () => controller.abort(timeout.aborted ? timeout.reason : options?.signal?.reason);
    timeout.addEventListener('abort', abort, { once: true });
    options?.signal?.addEventListener('abort', abort, { once: true });
    cleanups.push(() => { timeout.removeEventListener('abort', abort); options?.signal?.removeEventListener('abort', abort); });
    return fetch(root + path, { signal: controller.signal, method, headers: { Authorization: `Bearer ${session.access_token}`, "Content-Type": "application/json" }, body: body === undefined ? undefined : JSON.stringify(body) });
  };
  try {
    let response = await send();
    if (response.status === 401) {
      activeTimeout = undefined;
      enter('session'); checkCancelled();
      const { error } = await supabase.auth.refreshSession();
      checkCancelled();
      if (!error) response = await send();
    }
    if (!response.ok) {
      const retryAfter = response.headers.get('Retry-After');
      const seconds = retryAfter && /^\d+$/.test(retryAfter) ? Number(retryAfter) * 1000 : NaN;
      const date = retryAfter ? Date.parse(retryAfter) - Date.now() : NaN;
      const delay = Number.isFinite(seconds) ? seconds : Number.isFinite(date) ? Math.max(0, date) : undefined;
      throw new GemaRequestError('http', 'fetch', response.status, delay);
    }
    enter('body');
    const result = await response.json();
    checkCancelled();
    return result;
  } catch (error) {
    // Transports may report AbortError for both causes. The caller's cancellation
    // takes priority so a departed screen/account can never schedule a retry.
    if (options?.signal?.aborted) throw new GemaRequestError('cancelled', phase);
    if (activeTimeout?.aborted) throw new GemaRequestError('timeout', phase);
    throw classifyRequestError(error, phase);
  }
  finally { cleanups.forEach(cleanup => cleanup()); }
}
export async function getRamonProgress(): Promise<RamonProgress> {
  const result = await request("");
  if (!Number.isInteger(result.streakDays) || result.streakDays < 0 || !Array.isArray(result.days) ||
      typeof result.today !== "string" || typeof result.checkedToday !== "boolean") throw new Error("Sequência inválida");
  return result;
}
export const visitRamon = async (): Promise<{ moment: "welcome_back" | null }> => request("/visit", "POST");
